/**
 * 知识库加载与查询：绑定字段建议的知识库。
 *
 * 知识库来源（内置静态 JSON 已废弃，src/data/binding-dict.json 已删除）：
 *   - 动态提取（高优先级）：从范例/用户报表 XML 经 parseReport → IR → mineFromIr 提取
 *   - 用户配置的 dictPath JSON（可选）：mine-dict 落盘的绑定知识库
 *
 * 查询优先级（自上而下）：
 *   1. patterns（标签+位置）→ 最精细
 *   2. labels（标签）→ 中等精度
 *   3. positions（位置）→ 粗粒度
 */

import { readFileSync } from 'node:fs';

/**
 * Zone 同义归一：模板侧（regions.ts）与参考报表侧（mineFromIr.ts）的 zone 词汇不完全一致。
 * 模板把中部上区标为 `body`、中部下区标为 `detail`；参考报表则把 0.05~0.9 统归 `detail`，
 * 仅底部聚合区标 `footer`。无 label 套打走 positions 层（zone+side 键）锚定时，
 * 模板的 `body` 在参考报表 positions 里查不到，导致结构同构锚定失效（userAnchorHitCount=0）。
 * 故查询时按同义表尝试候选 zone，提升跨源命中率。
 */
export const ZONE_ALIASES: Record<string, string[]> = {
  body: ['detail', 'footer'],
  detail: ['body'],
  footer: ['body'],
  header: [],
};

import type { loadUserReportBindings } from '../../scripts/load-user-reports.js';
import type { BindingDict, BindingDictEntry } from './mineFromIr.js';
import { mergeBindingDicts } from './binding-dict-merger.js';
import { getLogger } from '../logger.js';

const log = getLogger('core:template');

export type { BindingDict, BindingDictEntry } from './mineFromIr.js';

export interface BindingSuggestion {
  field: string;
  confidence: number;
  source: 'pattern' | 'label' | 'position' | 'userPattern';
}

/**
 * 知识库加载器与查询接口
 */
export class BindingDictLoader {
  private dict: BindingDict | null = null;
  private userDict: BindingDict | null = null;
  private aliasDict: BindingDict | null = null;
  private mergedDict: BindingDict | undefined = undefined;
  private path: string;

  constructor(dictPath: string) {
    this.path = dictPath;
  }

  /**
   * 打印加载成功日志（含知识库规模），便于在 LLM/终端输出中识别是否真正调用了 binding-dict.json
   */
  private logLoaded(label: string, dict: BindingDict): void {
    const { labels, positions, patterns, version } = dict;
    log.info(
      `📖 ${label}已加载 (labels=${Object.keys(labels).length}, positions=${Object.keys(positions).length}, patterns=${Object.keys(patterns).length}, version=${version})`,
    );
  }

  /**
   * 返回空知识库（无静态 JSON、无动态源时使用）。
   */
  private static emptyDict(): BindingDict {
    return {
      labels: {},
      positions: {},
      patterns: {},
      version: 0,
      miningDate: '',
      exampleCount: 0,
      bindingCount: 0,
    };
  }

  /**
   * 从文件加载知识库（同步）。
   * 未配置 dictPath（空字符串）时静默返回空知识库，不产生"加载失败"噪音。
   */
  loadSync(): BindingDict {
    if (this.dict) return this.dict;
    if (!this.path) {
      log.debug('未配置 bindings.dictPath，使用空知识库');
      this.dict = BindingDictLoader.emptyDict();
      return this.dict;
    }

    try {
      const content = readFileSync(this.path, 'utf-8');
      this.dict = JSON.parse(content) as BindingDict;
      this.logLoaded('绑定知识库', this.dict);
      return this.dict;
    } catch (error) {
      log.warn(`⚠️ 知识库加载失败 (${this.path}): ${(error as Error).message}`);
      this.dict = BindingDictLoader.emptyDict();
      return this.dict;
    }
  }

  /**
   * 从文件加载知识库（异步）。
   * 未配置 dictPath（空字符串）时静默返回空知识库。
   */
  async load(): Promise<BindingDict> {
    if (this.dict) return this.dict;
    if (!this.path) {
      log.debug('未配置 bindings.dictPath，使用空知识库');
      this.dict = BindingDictLoader.emptyDict();
      return this.dict;
    }

    try {
      const fs = await import('node:fs/promises');
      const content = await fs.readFile(this.path, 'utf-8');
      this.dict = JSON.parse(content) as BindingDict;
      this.logLoaded('绑定知识库', this.dict);
      return this.dict;
    } catch (error) {
      log.warn(`⚠️ 知识库加载失败 (${this.path}): ${(error as Error).message}`);
      this.dict = BindingDictLoader.emptyDict();
      return this.dict;
    }
  }

  /**
   * 动态加载用户报表知识库（用户已有报表 XML → parseReport → IR → 绑定知识）。
   */
  async loadUserReports(userReportsDir: string): Promise<BindingDict> {
    try {
      const { loadUserReportBindings } = await import(
        '../../scripts/load-user-reports.js'
      );
      const loaded = await loadUserReportBindings(userReportsDir);
      this.userDict = loaded;
      this.logLoaded('用户报表知识库', loaded);
      // 清除合并缓存
      this.mergedDict = undefined;
      return loaded;
    } catch (error) {
      log.warn(
        `⚠️ 用户报表加载失败 (${userReportsDir}): ${(error as Error).message}`,
      );
      this.userDict = BindingDictLoader.emptyDict();
      return this.userDict;
    }
  }

  /**
   * 动态加载范例/示例目录知识库（bindings.examplesDir → XML→IR→绑定知识）。
   * 与 loadUserReports 共享同一实现，语义上用于"从范例报表提取"。
   */
  async loadFromDynamic(examplesDir: string): Promise<BindingDict> {
    return this.loadUserReports(examplesDir);
  }

  /**
   * 加载数据集字段别名知识库（bindings.aliasDir → *.json 实时补充）。
   * 别名是用户明确定义的"标签/别名 → 字段"强映射，命中时由 query 锚定胜出，
   * 用于解决模板标签与范例 label 体系不一致导致的零命中问题。
   */
  async loadAliases(aliasDir: string): Promise<BindingDict> {
    try {
      const { loadFieldAliases } = await import(
        '../../scripts/load-field-aliases.js'
      );
      const loaded = await loadFieldAliases(aliasDir);
      this.aliasDict = loaded;
      this.logLoaded('字段别名', loaded);
      // 清除合并缓存，使别名并入合并知识库
      this.mergedDict = undefined;
      return loaded;
    } catch (error) {
      log.warn(
        `⚠️ 字段别名加载失败 (${aliasDir}): ${(error as Error).message}`,
      );
      this.aliasDict = BindingDictLoader.emptyDict();
      return this.aliasDict;
    }
  }

  /**
   * 获取合并的知识库（用户报表优先，字段别名作为最强来源补充）
   */
  getMergedDict(): BindingDict {
    if (this.mergedDict) return this.mergedDict;

    const builtinDict = this.loadSync();
    let merged = mergeBindingDicts(this.userDict ?? builtinDict, builtinDict, {
      userWeight: 1.3,
    });
    // 字段别名是用户明确定义的真值映射，作为最高优先级并入（权重最高）
    if (this.aliasDict) {
      merged = mergeBindingDicts(this.aliasDict, merged, {
        userWeight: 2.0, // 别名显著压过其他来源
        builtinWeight: 1.0,
      });
    }
    this.mergedDict = merged;
    this.logLoaded('合并知识库', merged);

    return merged;
  }

  /**
   * 查询：标签 + 位置 → 建议字段列表
   *
   * @param label 标签名（如 "金额"）
   * @param zone 位置纵向分类（如 "detail"）
   * @param side 位置横向分类（如 "center"）
   * @returns 排序后的建议列表（confidence 从高到低）
   */
  query(label?: string, zone?: string, side?: string): BindingSuggestion[] {
    // 如果加载了用户报表，使用合并知识库；否则使用内置知识库
    const dict = this.userDict ? this.getMergedDict() : this.loadSync();
    const suggestions: BindingSuggestion[] = [];

    // 优先级 -1：字段别名精确命中——别名是用户明确定义的「标签→字段」强真值，
    // 命中即锚定胜出（source:'userPattern'，置信度 1.0），直接返回不再降级查其他层。
    if (label && this.aliasDict) {
      const aliasEntry = this.aliasDict.labels[label];
      if (aliasEntry) {
        aliasEntry.fields.forEach((field, idx) => {
          if (!suggestions.some((s) => s.field === field)) {
            suggestions.push({
              field,
              confidence: aliasEntry.confidence[idx] ?? 1.0,
              source: 'userPattern',
            });
          }
        });
      }
      if (suggestions.length > 0) {
        log.debug(
          `字段别名「${label}」命中 ${suggestions.length} 条锚定建议`,
        );
        return suggestions;
      }
    }

    // 优先级 0：参考报表（userDict）精确绑定——供 scorer 将其锚定为高置信（直接胜出）。
    // 参考报表的绑定是明确真值（confidence 0.95），但合并后 dict 丢失来源信息，
    // 故单独对 userDict 查询，命中即标记 source:'userPattern'。
    //
    // 两路：
    //   (a) 有 label：走 pattern 层（标签+位置）精确锚定——最精细；
    //   (b) 无 label（套打单据常见：数据位是无标签填充文本）：退而走 positions 层
    //       （仅 zone+side），锚定参考报表同位置簇的字段。这是"结构同构"信号，
    //       让无标签数据位也能复用参考报表的字段绑定，避免 suggest 退化为噪声。
    if (this.userDict && zone && side) {
      if (label) {
        const candidateZones = [zone, ...(ZONE_ALIASES[zone] || [])];
        let userEntry: typeof this.userDict.patterns[string] | undefined;
        for (const z of candidateZones) {
          userEntry = this.userDict.patterns[`${label}@${z}_${side}`];
          if (userEntry) break;
        }
        if (userEntry) {
          userEntry.fields.forEach((field, idx) => {
            suggestions.push({
              field,
              confidence: userEntry.confidence[idx] ?? 0.95,
              source: 'userPattern',
            });
          });
        }
      } else {
        // 无 label：套打同构场景，用 zone+side 在参考报表 positions 层锚定。
        // 该簇字段来自有 label 的范例，迁移到同位置的无标签数据位。
        // 候选 zone 含同义词（body↔detail↔footer 跨源归一），提升无标签套打命中率。
        const candidateZones = [zone, ...(ZONE_ALIASES[zone] || [])];
        for (const z of candidateZones) {
          const posKey = `${z}_${side}`;
          const userEntry = this.userDict.positions[posKey];
          if (!userEntry) continue;
          userEntry.fields.forEach((field, idx) => {
            if (suggestions.some((s) => s.field === field)) return;
            suggestions.push({
              field,
              confidence: (userEntry.confidence[idx] ?? 0.95) * 0.85, // 无 label 略降权
              source: 'userPattern',
            });
          });
          if (suggestions.length > 0) break;
        }
      }
    }

    // 优先级 1：pattern（标签+位置）——对已由 userPattern 命中的字段去重，避免重复建议
    if (label && zone && side) {
      const patternKey = `${label}@${zone}_${side}`;
      const entry = dict.patterns[patternKey];
      if (entry) {
        entry.fields.forEach((field, idx) => {
          if (suggestions.some((s) => s.field === field)) return;
          suggestions.push({
            field,
            confidence: entry.confidence[idx] ?? 0.5,
            source: 'pattern',
          });
        });
      }
    }

    // 优先级 2：label（标签）
    if (label) {
      const entry = dict.labels[label];
      if (entry) {
        entry.fields.forEach((field, idx) => {
          if (!suggestions.some((s) => s.field === field)) {
            suggestions.push({
              field,
              confidence: (entry.confidence[idx] ?? 0.5) * 0.8, // 降权
              source: 'label',
            });
          }
        });
      }
    }

    // 优先级 3：position（位置）
    if (zone && side) {
      const posKey = `${zone}_${side}`;
      const entry = dict.positions[posKey];
      if (entry) {
        entry.fields.forEach((field, idx) => {
          if (!suggestions.some((s) => s.field === field)) {
            suggestions.push({
              field,
              confidence: (entry.confidence[idx] ?? 0.5) * 0.6, // 降权
              source: 'position',
            });
          }
        });
      }
    }

    // 排序：confidence 从高到低
    suggestions.sort((a, b) => b.confidence - a.confidence);

    if (suggestions.length > 0) {
      log.debug(
        `标签「${label ?? ''}」@${zone ?? ''}_${side ?? ''} 命中 ${suggestions.length} 条知识库建议（${suggestions
          .slice(0, 3)
          .map((s) => `${s.field}:${(s.confidence * 100).toFixed(0)}%`)
          .join(', ')}）`,
      );
    }

    return suggestions;
  }

  /**
   * 提取参考报表实际绑定过的字段全集（按参考报表优先级排序）。
   * 用于无 label 套打场景的"样式/类型软匹配"：当 positions 层无同位置锚点时，
   * 退而用这些"参考报表真实出现过的字段"作为软锚点，结合待绑格样式/类型信号排序，
   * 避免无标签数据位退化为纯噪声（见 llm.out 第 262 行现象）。
   *
   * @returns 字段列表，每项含参考报表权重（userPattern 字段权重高，纯 label 字段权重低）
   */
  getKnownFields(): { field: string; weight: number }[] {
    if (!this.userDict) return [];
    const map = new Map<string, number>();
    const add = (field: string, w: number) => {
      map.set(field, Math.max(map.get(field) ?? 0, w));
    };
    // positions 层（无 label 同位置簇，权重最高）
    for (const key of Object.keys(this.userDict.positions)) {
      const e = this.userDict.positions[key];
      if (!e) continue;
      e.fields.forEach((f, i) => add(f, (e.confidence[i] ?? 0.95) * 0.85));
    }
    // patterns 层（label@zone_side，有 label 的明确绑定）
    for (const key of Object.keys(this.userDict.patterns)) {
      const e = this.userDict.patterns[key];
      if (!e) continue;
      e.fields.forEach((f, i) => add(f, (e.confidence[i] ?? 0.95)));
    }
    // labels 层（标签→字段，弱于绑定）
    for (const label of Object.keys(this.userDict.labels)) {
      const e = this.userDict.labels[label];
      if (!e) continue;
      e.fields.forEach((f, i) => add(f, (e.confidence[i] ?? 0.5) * 0.6));
    }
    return Array.from(map.entries())
      .map(([field, weight]) => ({ field, weight }))
      .sort((a, b) => b.weight - a.weight);
  }

  /**
   * 无 label 套打样式软匹配：从参考报表已知字段中挑出候选，按参考报表权重排序返回。
   * 这是 positions 层结构同构锚定失败时的回退通道，使无标签数据位也能拿到
   * 参考报表相关的可建议字段（而非仅泛化 availableFields 的纯噪声）。
   * 具体排序由 scorer 结合待绑格样式/类型信号完成。
   */
  queryByStyle(): { field: string; weight: number }[] {
    return this.getKnownFields();
  }

  /**
   * 查询：仅按标签
   */
  queryByLabel(label: string): BindingSuggestion[] {
    return this.query(label);
  }

  /**
   * 查询：仅按位置
   */
  queryByPosition(zone: string, side: string): BindingSuggestion[] {
    return this.query(undefined, zone, side);
  }

  /**
   * 获取知识库统计信息
   */
  getStats(): {
    labelCount: number;
    positionCount: number;
    patternCount: number;
    totalBindings: number;
    version: number;
    miningDate: string;
    hasUserReports: boolean;
  } {
    const dict = this.userDict ? this.getMergedDict() : this.loadSync();
    return {
      labelCount: Object.keys(dict.labels).length,
      positionCount: Object.keys(dict.positions).length,
      patternCount: Object.keys(dict.patterns).length,
      totalBindings: dict.bindingCount || 0,
      version: dict.version,
      miningDate: dict.miningDate,
      hasUserReports: !!this.userDict,
    };
  }

  /**
   * 获取原始知识库对象
   */
  getDict(): BindingDict {
    return this.userDict ? this.getMergedDict() : this.loadSync();
  }
}

/**
 * 全局单例实例（默认路径）
 */
let globalLoader: BindingDictLoader | null = null;

/**
 * 获取全局加载器实例。
 *
 * 注意：内置静态知识库已废弃（src/data/binding-dict.json 已删除），
 * 知识库完全来自动态提取（examplesDir）或用户配置的 dictPath；
 * 默认空字符串表示不加载任何静态 JSON，直接返回空知识库。
 */
export function getBindingDictLoader(dictPath: string = ''): BindingDictLoader {
  if (!globalLoader) {
    globalLoader = new BindingDictLoader(dictPath);
  }
  return globalLoader;
}

/**
 * 快捷查询函数（使用全局加载器）
 */
export function suggestBindings(
  label?: string,
  zone?: string,
  side?: string,
  dictPath?: string,
): BindingSuggestion[] {
  const loader = getBindingDictLoader(dictPath);
  return loader.query(label, zone, side);
}
